import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Loader2, Lock } from 'lucide-react';
import {
  getLscArchiveAutopublishEligibility,
  getLscArchiveAutopublishSafety,
  getLscArchiveAutopublishSettings,
} from '../../api';
import { archiveAutopublishBlockerCustomerLabels } from '../../archiveAutopublishBlockerCopy';
import {
  ARCHIVE_AUTOPUBLISH_WIZARD_STAGE2_EXPLAINER_ITEMS,
  ARCHIVE_RETENTION_COMMISSIONER_COPY,
} from '../../archiveRetentionCopy';
import { archiveAutopublishSettingsCustomerCopy } from '../../commandCenterCopy';
import { lscPath } from '../../routing';
import type {
  LscArchiveAutopublishEligibility,
  LscArchiveAutopublishKillSwitch,
  LscArchiveAutopublishSettings,
} from '../../types';

export type AutomationActivationArchiveAutopublishPanelProps = {
  leagueId: string;
};

function postureBadgeLabel(mode: string | undefined | null): string {
  switch ((mode || 'off').trim().toLowerCase()) {
    case 'dry_run':
      return 'Dry run';
    case 'controlled_apply':
      return 'Controlled publishing locked';
    case 'off':
    default:
      return 'Off';
  }
}

const AutomationActivationArchiveAutopublishPanel: React.FC<
  AutomationActivationArchiveAutopublishPanelProps
> = ({ leagueId }) => {
  const [settings, setSettings] = useState<LscArchiveAutopublishSettings | null>(null);
  const [eligibility, setEligibility] = useState<LscArchiveAutopublishEligibility | null>(null);
  const [safety, setSafety] = useState<LscArchiveAutopublishKillSwitch | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const settingsHref = lscPath(`/leagues/${leagueId}/settings/archive-automation`);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [settingsPayload, eligibilityPayload, safetyPayload] = await Promise.all([
        getLscArchiveAutopublishSettings(leagueId),
        getLscArchiveAutopublishEligibility(leagueId),
        getLscArchiveAutopublishSafety(leagueId),
      ]);
      setSettings(settingsPayload);
      setEligibility(eligibilityPayload);
      setSafety(safetyPayload);
    } catch {
      setLoadError('Unable to load archive automation posture right now.');
      setSettings(null);
      setEligibility(null);
      setSafety(null);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const settingsMode = settings?.autopublish_mode || 'off';
  const modeCopy = archiveAutopublishSettingsCustomerCopy(settingsMode);
  const controlledLocked =
    settingsMode === 'controlled_apply' || settingsMode === 'controlled_apply_locked';

  const blockerLabels = useMemo(
    () =>
      archiveAutopublishBlockerCustomerLabels([
        ...(eligibility?.blocking_reasons || []),
        ...(safety?.disabled_reason_codes || []),
      ]),
    [eligibility?.blocking_reasons, safety?.disabled_reason_codes]
  );

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-secondary-400"
        data-testid="automation-wizard-archive-autopublish-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading archive automation posture…
      </div>
    );
  }

  return (
    <div
      className="card space-y-4 min-w-0 overflow-x-hidden"
      data-testid="automation-wizard-archive-autopublish-panel"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h4 className="text-sm font-semibold text-white">Archive automation (Stage 2)</h4>
        <span
          className="inline-flex w-fit items-center rounded-full border border-secondary-600 bg-secondary-800/60 px-2.5 py-1 text-xs font-semibold text-secondary-200"
          data-testid="automation-wizard-archive-autopublish-badge"
        >
          {postureBadgeLabel(settingsMode)}
        </span>
      </div>

      {loadError ? (
        <p className="text-sm text-amber-200" data-testid="automation-wizard-archive-autopublish-error">
          {loadError}
        </p>
      ) : (
        <>
          <p className="text-sm text-secondary-200" data-testid="automation-wizard-archive-autopublish-mode">
            {modeCopy}
          </p>

          <div
            className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-3 py-3 space-y-2"
            data-testid="automation-wizard-archive-autopublish-explainer"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Stage 2 explainer
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-secondary-300">
              {ARCHIVE_AUTOPUBLISH_WIZARD_STAGE2_EXPLAINER_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {controlledLocked ? (
            <p
              className="flex items-start gap-2 text-sm text-secondary-400"
              data-testid="automation-wizard-archive-autopublish-controlled-locked"
            >
              <Lock className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
              Controlled publishing is locked until post-launch Stage 2. Manual review remains the fallback.
            </p>
          ) : null}

          {blockerLabels.length > 0 ? (
            <div
              className="space-y-2"
              data-testid="automation-wizard-archive-autopublish-blockers"
            >
              <p className="text-xs font-medium text-secondary-400">Current eligibility notes</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-secondary-300">
                {blockerLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="text-xs text-secondary-500">{ARCHIVE_RETENTION_COMMISSIONER_COPY}</p>
        </>
      )}

      <Link
        to={settingsHref}
        className="btn-secondary text-sm inline-flex w-full sm:w-auto justify-center min-h-[44px] items-center"
        data-testid="automation-wizard-archive-automation-settings-link"
      >
        Open archive automation settings
      </Link>
    </div>
  );
};

export default AutomationActivationArchiveAutopublishPanel;
